interface OptionFlowTimestampSource {
  lastExchangeTimestamp?: unknown;
  exchangeTimestamp?: unknown;
  createdTimestamp?: number | null;
  createdAt?: string | null;
}

// A grouped trade can keep filling for minutes (the producer has no span cap), but not past
// its session. A larger gap means the value is not this trade's last print.
const MAX_GROUP_SPAN_MS = 24 * 60 * 60 * 1000;

function validMilliseconds(value: number): number | undefined {
  return Number.isFinite(value) && value > 0 && Number.isFinite(new Date(value).getTime())
    ? value
    : undefined;
}

/** Epoch milliseconds from a source timestamp in seconds, milliseconds, microseconds or nanoseconds. */
function parseSourceTimestampMs(source: unknown): number | undefined {
  if (
    !(typeof source === 'number' || (typeof source === 'string' && source.trim() !== '')) ||
    !Number.isFinite(Number(source)) ||
    Number(source) <= 0 ||
    Number(source) >= 2 ** 64 // Databento's undefined timestamp is u64::MAX.
  ) {
    return undefined;
  }

  const timestamp = Number(source);
  // Legacy sources have used each epoch unit; Databento supplies nanoseconds.
  let milliseconds =
    timestamp >= 1e17
      ? timestamp / 1e6
      : timestamp >= 1e14
      ? timestamp / 1e3
      : timestamp < 1e11
      ? timestamp * 1e3
      : timestamp;
  // Truncate integer source strings before converting their high-precision
  // units to Number. Rounding 19-digit nanoseconds first can move an execution
  // just before a minute boundary into the following chart bucket.
  if (typeof source === 'string' && /^\d+$/.test(source.trim())) {
    const digits = source.trim();
    if (timestamp >= 1e17) milliseconds = Number(digits.slice(0, -6));
    else if (timestamp >= 1e14) milliseconds = Number(digits.slice(0, -3));
  }
  return validMilliseconds(Math.floor(milliseconds));
}

/**
 * When the trade finished executing: the exchange time of its last print. A sweep is only
 * complete, and its size and premium known, at its last print. Rows without a usable last
 * print (older rows, producer flag off) use the first print, then ingestion time.
 */
export function getOptionFlowTimestampMs(trade: OptionFlowTimestampSource): number | undefined {
  const firstPrint = parseSourceTimestampMs(trade.exchangeTimestamp);
  const lastPrint = parseSourceTimestampMs(trade.lastExchangeTimestamp);
  if (
    lastPrint !== undefined &&
    (firstPrint === undefined ||
      (lastPrint >= firstPrint && lastPrint - firstPrint <= MAX_GROUP_SPAN_MS))
  ) {
    return lastPrint;
  }
  if (firstPrint !== undefined) return firstPrint;

  if (typeof trade.createdTimestamp === 'number') {
    const derivedTime = validMilliseconds(trade.createdTimestamp);
    if (derivedTime !== undefined) return derivedTime;
  }
  return typeof trade.createdAt === 'string'
    ? validMilliseconds(Date.parse(trade.createdAt))
    : undefined;
}

/**
 * Execution time for a derived trade. getDerivedTradeValues resolves it once per row as
 * `tradeTimestamp`; filters, sorts and charts reuse it instead of re-parsing the source.
 */
export function getTradeExecutionMs(
  trade: OptionFlowTimestampSource & { tradeTimestamp?: number },
): number | undefined {
  return trade.tradeTimestamp ?? getOptionFlowTimestampMs(trade);
}
